import { parsePositiveInt } from "./reservationRowUtils";

function normalizeHourMinuteLabel(value) {
   const m = String(value || "")
      .trim()
      .match(/^(\d{1,2}):(\d{2})$/);
   if (!m) return "";
   return `${String(Number(m[1])).padStart(2, "0")}:${m[2]}`;
}

export function pickBlackoutField(item, keys) {
   if (!item || typeof item !== "object") return null;
   for (const key of keys) {
      const value = item?.[key];
      if (value !== null && value !== undefined && value !== "") return value;
   }
   return null;
}

export function parseUltraBlockedCellKey(value) {
   const [dayRaw = "", instRaw = "", slotRaw = ""] = String(value || "").split("|");
   const dayIndex = Number(dayRaw);
   const instructorId = String(instRaw || "").trim();
   const slotLabel = normalizeHourMinuteLabel(slotRaw);
   if (!Number.isFinite(dayIndex) || dayIndex < 0) return null;
   if (!instructorId || !slotLabel) return null;
   return { dayIndex, instructorId, slotLabel };
}

export function getBlackoutInstructorId(item) {
   return (
      pickBlackoutField(item, [
         "instructorId",
         "instructor_id",
         "instructorID",
         "instructor_id_fk",
      ]) ??
      item?.instructor?.id ??
      item?.instructor?.instructorId ??
      item?.instructor?.instructor_id ??
      null
   );
}

export function getBlackoutId(item) {
   const id = pickBlackoutField(item, ["id", "_id", "blackoutId", "blackout_id"]);
   return parsePositiveInt(id);
}

export function getBlackoutType(item) {
   const rawType = String(
      pickBlackoutField(item, [
         "type",
         "Type",
         "blackoutType",
         "blackout_type",
      ]) || "",
   )
      .trim()
      .toUpperCase();
   if (rawType === "REPEAT" || rawType.includes("REPEAT")) return "REPEAT";

   if (
      pickBlackoutField(item, [
         "repeatEveryDays",
         "repeat_every_days",
         "repeatEveryDay",
         "repeat_every_day",
         "repeatIntervalDays",
         "repeat_interval_days",
      ]) != null
   ) {
      return "REPEAT";
   }

   return "SINGLE";
}

export function getBlackoutStartRaw(item) {
   const type = getBlackoutType(item);
   if (type === "REPEAT") {
      return pickBlackoutField(item, [
         "startDateTime",
         "start_date_time",
         "startDate",
         "start_date",
         "dateTime",
         "datetime",
         "date_time",
         "startTime",
         "start_time",
         "start",
         "date",
         "from",
         "begin",
      ]);
   }

   return pickBlackoutField(item, [
      "dateTime",
      "datetime",
      "date_time",
      "startTime",
      "start_time",
      "start",
      "date",
      "from",
      "begin",
   ]);
}

export function getBlackoutEndRaw(item) {
   return (
      pickBlackoutField(item, [
         "endDateTime",
         "end_date_time",
         "endDate",
         "end_date",
         "endTime",
         "end_time",
         "end",
         "to",
      ]) || null
   );
}

export function getBlackoutRepeatEveryDays(item) {
   const value = Number(
      pickBlackoutField(item, [
         "repeatEveryDays",
         "repeat_every_days",
         "repeatEveryDay",
         "repeat_every_day",
         "repeatIntervalDays",
         "repeat_interval_days",
      ]) || 1,
   );
   if (!Number.isFinite(value)) return 1;
   return Math.max(1, Math.trunc(value));
}

export function createBlackoutRowMatcher({
   resolvedDayIndex,
   resolvedSlotLabel,
   resolvedInstructorId,
   instructorIdForApi,
   expectedLocalKey,
   expectedHm,
   expectedDayMs,
   busyLocalKeyFromStored,
   getBlackoutStartRaw: getStartRaw = getBlackoutStartRaw,
   getBlackoutEndRaw: getEndRaw = getBlackoutEndRaw,
   getBlackoutType: getType = getBlackoutType,
   getBlackoutRepeatEveryDays: getRepeatDays = getBlackoutRepeatEveryDays,
   getBlackoutInstructorId: getInstructorId = getBlackoutInstructorId,
}) {
   const DAY_MS = 24 * 60 * 60 * 1000;
   const numericInstructorId = parsePositiveInt(resolvedInstructorId);
   const instructorAliases = new Set([String(resolvedInstructorId || "").trim()]);
   if (numericInstructorId != null) {
      instructorAliases.add(String(numericInstructorId));
   }
   if (instructorIdForApi != null) {
      instructorAliases.add(String(instructorIdForApi));
   }

   const instructorAliasMatches = (instructorIdRaw) => {
      const instructorId = String(instructorIdRaw || "").trim();
      if (!instructorId) return false;
      if (instructorAliases.has(instructorId)) return true;
      const numericId = parsePositiveInt(instructorId);
      return numericId != null && instructorAliases.has(String(numericId));
   };

   return (row) => {
      if (!row || typeof row !== "object") return false;

      const localSlot = parseUltraBlockedCellKey(
         pickBlackoutField(row, [
            "localSlotKey",
            "local_slot_key",
            "slotKey",
            "slot_key",
            "_slotKey",
         ]),
      );
      if (localSlot) {
         if (!instructorAliasMatches(localSlot.instructorId)) return false;
         return (
            Number(localSlot.dayIndex) === resolvedDayIndex &&
            String(localSlot.slotLabel || "").trim() === resolvedSlotLabel
         );
      }

      if (!instructorAliasMatches(getInstructorId(row))) return false;

      const rowStart = getStartRaw(row);
      if (!rowStart) return false;
      const rowType = getType(row);
      if (rowType !== "REPEAT") {
         return busyLocalKeyFromStored(rowStart) === expectedLocalKey;
      }

      const rowEnd = getEndRaw(row) || rowStart;
      const rowStartLocalKey = busyLocalKeyFromStored(rowStart);
      const rowEndLocalKey = busyLocalKeyFromStored(rowEnd);
      const [rowStartDayKey = "", rowStartHmRaw = ""] = String(rowStartLocalKey).split(
         "|",
      );
      const [rowEndDayKey = ""] = String(rowEndLocalKey).split("|");
      const rowStartHm = normalizeHourMinuteLabel(rowStartHmRaw);
      if (!rowStartDayKey || !rowEndDayKey || !rowStartHm) return false;
      if (!expectedHm || rowStartHm !== expectedHm) return false;
      if (!Number.isFinite(expectedDayMs)) return false;

      const rowStartDayMs = new Date(`${rowStartDayKey}T00:00:00Z`).getTime();
      const rowEndDayMs = new Date(`${rowEndDayKey}T00:00:00Z`).getTime();
      if (!Number.isFinite(rowStartDayMs) || !Number.isFinite(rowEndDayMs)) {
         return false;
      }
      if (rowEndDayMs < rowStartDayMs) return false;
      if (expectedDayMs < rowStartDayMs || expectedDayMs > rowEndDayMs) {
         return false;
      }

      const stepDays = getRepeatDays(row);
      const normalizedStepDays = Number.isFinite(stepDays)
         ? Math.max(1, Math.trunc(stepDays))
         : 1;
      const diffDays = Math.floor((expectedDayMs - rowStartDayMs) / DAY_MS);
      return diffDays >= 0 && diffDays % normalizedStepDays === 0;
   };
}
